if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      static MEDIACLIP_RAW_PROPS = [
        'startMonth',
        'startYear',
        'weekBeginsOn',
        'eventList',
        'projectName',
        'type',
        'appleProjectId',
        'projectSource',
        'shareProjectId',
        'userId',
        'userid',
        'creationDate',
      ];

      constructor() {
        super();
        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) {
          console.error('ProductForm: form element not found');
          return;
        }

        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) {
          console.error('ProductForm: submit button not found');
          return;
        }

        this.submitButtonText = this.submitButton.querySelector('span');
        if (!this.submitButtonText) {
          console.warn('ProductForm: submit button text span not found');
        }

        // Safe initialization of variantIdInput
        try {
          const variantInput = this.variantIdInput;
          if (variantInput) {
            variantInput.disabled = false;
          }
        } catch (e) {
          console.warn('ProductForm: Unable to initialize variant input', e);
        }

        // Bound once per element. connectedCallback() runs again whenever the
        // node is re-inserted (quick-add modal, section re-render); without this
        // guard a reconnect added a second listener and fired two /cart/add
        // requests per click.
        if (!this.boundOnSubmitHandler) {
          this.boundOnSubmitHandler = this.onSubmitHandler.bind(this);
          this.form.addEventListener('submit', this.boundOnSubmitHandler);
        } else {
          this.form.removeEventListener('submit', this.boundOnSubmitHandler);
          this.form.addEventListener('submit', this.boundOnSubmitHandler);
        }

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      normalizeSource(source) {
        const raw = source === undefined || source === null ? '' : String(source);
        const normalized = raw.trim().toLowerCase();

        if (!normalized) {
          return window.Capacitor || window.cordova ? 'ios' : 'web';
        }

        if (normalized === 'web' || normalized === 'ios') return normalized;

        if (
          normalized.includes('ios') ||
          normalized.includes('capacitor') ||
          normalized.includes('cordova')
        ) {
          return 'ios';
        }

        if (normalized.includes('web') || normalized.includes('browser')) {
          return 'web';
        }

        return normalized;
      }

      shouldTransformCheckoutProperties() {
        // Any source that is not explicitly recognised still gets the raw
        // MediaClip properties hidden. Returning false for an unknown value
        // (e.g. a native wrapper reporting 'android') meant projectName,
        // userId, appleProjectId, shareProjectId and creationDate were sent
        // through to the hosted checkout as visible line-item properties.
        return true;
      }

      shouldShowProjectNameAtCheckout(source) {
        const normalizedSource = this.normalizeSource(source);
        return normalizedSource !== 'web' && normalizedSource !== 'ios';
      }

      transformCheckoutPropertiesInFormData(formData) {
        if (!this.shouldTransformCheckoutProperties()) return;

        const source = this.normalizeSource(typeof window.getSource === 'function' ? window.getSource() : 'web');
        let hasRawProperty = false;

        ProductForm.MEDIACLIP_RAW_PROPS.forEach((key) => {
          const rawKey = `properties[${key}]`;
          if (!formData.has(rawKey)) return;

          const value = formData.get(rawKey);
          hasRawProperty = true;

          formData.delete(rawKey);
          formData.delete(`properties[_${key}]`);
          formData.append(`properties[_${key}]`, value);

          if (key === 'projectName') {
            formData.delete('properties[Project Name]');
            if (this.shouldShowProjectNameAtCheckout(source)) {
              formData.append('properties[Project Name]', value);
            }
          }
        });

        if (!hasRawProperty) return;

        formData.delete('properties[_source]');
        formData.append('properties[_source]', source);
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        if (!this.submitButton) {
          console.error('ProductForm: submitButton not initialized');
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.transformCheckoutPropertiesInFormData(formData);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const runCartMutation =
          typeof window.__mimeoRunCartMutation === 'function'
            ? window.__mimeoRunCartMutation
            : (mutationCallback) => Promise.resolve().then(mutationCallback);

        runCartMutation(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) {
                // Not a sold-out error, re-enable button for retry
                this.error = false;
                return;
              }
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
           }
           else {
            const source = this.normalizeSource(typeof window.getSource === 'function' ? window.getSource() : 'web');
            const shouldUseCartPageFlow = source === 'web' || source === 'ios';

            if (shouldUseCartPageFlow) {
              // Use the locale-aware route the theme already publishes rather
              // than re-deriving the prefix from the path: the old regex only
              // matched `xx` / `xx-yyy`, so any other market subfolder lost its
              // prefix and the shopper landed on the wrong-locale cart.
              let cartPath = '/cart';
              if (typeof routes !== 'undefined' && routes && routes.cart_url) {
                cartPath = routes.cart_url;
              } else {
                const pathMatch = window.location.pathname.match(/^\/([a-z]{2}(-[a-z]{2,})?)(?:\/|$)/i);
                const locale = pathMatch ? pathMatch[1] : '';
                cartPath = locale ? `/${locale}/cart` : '/cart';
              }

              let cartUrl = cartPath;
              try {
                const sourceFromUrl = new URLSearchParams(window.location.search).get('source');
                const sourceFromStorage = sessionStorage.getItem('mimeo_cart_source');
                const resolvedSource = sourceFromUrl || sourceFromStorage;

                if (resolvedSource) {
                  // Persist so the parameter survives the next add-to-cart from
                  // a product page that no longer carries ?source= in its URL.
                  // Nothing wrote this key before, so the storage fallback read
                  // above could never fire.
                  try { sessionStorage.setItem('mimeo_cart_source', resolvedSource); } catch (_pe) {}
                  cartUrl = cartPath + '?source=' + encodeURIComponent(resolvedSource);
                }
              } catch (_e) {
                // Fall back to cart path without source query parameter.
              }

              try { sessionStorage.setItem('__mimeo_needs_cart_normalize', 'true'); } catch (_se) {}
              window.location = cartUrl;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            if (!this.cart) {
              window.location = '/checkout';
              return;
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          }
          })
          .catch((e) => {
            console.error(e);
            // On network error, allow retry
            this.error = false;
          })
          .finally(() => {
            if (!this.submitButton) return;

            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Only keep aria-disabled if genuinely sold out (this.error = true)
            // For all other cases, re-enable the button for retry
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            const spinner = this.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
